/**
 * Feature Flags
 *
 * Centralized feature flag management for controlling experimental features
 * and gradual rollouts.
 */
import { useEffect, useState } from 'react';

const FEATURE_FLAGS_CHANGED_EVENT = 'museflow_feature_flags_changed';

/**
 * Feature flag keys
 */
export enum FeatureFlag {
  /**
   * Use OSMD Phaser backend for music rendering instead of pre-rendered SVGs.
   * When enabled:
   * - MusicXML is rendered directly using OSMD's Phaser backend
   * - No SVG or JSON data is fetched from the backend
   * - NoteContainers are used for interactive note elements
   */
  USE_OSMD_PHASER_BACKEND = 'USE_OSMD_PHASER_BACKEND',
  USE_LESSON_ZOOM = 'USE_LESSON_ZOOM',
  USE_ERROR_FEEDBACK = 'USE_ERROR_FEEDBACK',
  USE_ADAPTIVE_TEMPO = 'USE_ADAPTIVE_TEMPO',
  USE_AUDIO_RECOGNITION = 'USE_AUDIO_RECOGNITION',
  USE_MOBILE_SIGHT_READING = 'USE_MOBILE_SIGHT_READING',
  USE_ANNOUNCEMENT_MODAL = 'USE_ANNOUNCEMENT_MODAL',
  USE_REPERTOIRE_CREATE = 'USE_REPERTOIRE_CREATE',
  USE_REPERTOIRE_LIBRARY = 'USE_REPERTOIRE_LIBRARY',
  USE_REPERTOIRE_PLAYBACK_MODES = 'USE_REPERTOIRE_PLAYBACK_MODES',
  USE_TRANSPOSE_SIGHT_READING_TRAINER = 'USE_TRANSPOSE_SIGHT_READING_TRAINER',
  USE_TRANSPOSE_MODE_CONVERSION = 'USE_TRANSPOSE_MODE_CONVERSION',
  USE_HISTORY_OF_PIANO_MUSIC = 'USE_HISTORY_OF_PIANO_MUSIC',
}

/**
 * Read a boolean from env, returning the fallback when the var is absent.
 * Uses process.env which Vite's envPlugin replaces at build time and Jest supports natively.
 */
function envBoolean(key: string, fallback: boolean): boolean {
  const value = process.env[key];
  if (value === undefined || value === '') return fallback;
  return value === 'true' || value === '1';
}

/**
 * Default values for feature flags
 */
const DEFAULT_FLAGS: Record<FeatureFlag, boolean> = {
  [FeatureFlag.USE_OSMD_PHASER_BACKEND]: true, // Enabled — OSMD Phaser backend active for lessons and repertoire
  [FeatureFlag.USE_LESSON_ZOOM]: envBoolean('REACT_APP_USE_LESSON_ZOOM', true),
  [FeatureFlag.USE_ERROR_FEEDBACK]: false,
  [FeatureFlag.USE_ADAPTIVE_TEMPO]: true,
  [FeatureFlag.USE_AUDIO_RECOGNITION]: envBoolean('REACT_APP_USE_AUDIO_RECOGNITION', false),
  [FeatureFlag.USE_MOBILE_SIGHT_READING]: true, // Enabled by default — mobile sight reading is now standard
  [FeatureFlag.USE_ANNOUNCEMENT_MODAL]: envBoolean('REACT_APP_USE_ANNOUNCEMENT_MODAL', false),
  [FeatureFlag.USE_REPERTOIRE_CREATE]: envBoolean('REACT_APP_USE_REPERTOIRE_CREATE', false),
  [FeatureFlag.USE_REPERTOIRE_LIBRARY]: envBoolean('REACT_APP_USE_REPERTOIRE_LIBRARY', false),
  [FeatureFlag.USE_REPERTOIRE_PLAYBACK_MODES]: envBoolean(
    'REACT_APP_USE_REPERTOIRE_PLAYBACK_MODES',
    false,
  ),
  [FeatureFlag.USE_TRANSPOSE_SIGHT_READING_TRAINER]: envBoolean(
    'REACT_APP_USE_TRANSPOSE_SIGHT_READING_TRAINER',
    false,
  ),
  [FeatureFlag.USE_TRANSPOSE_MODE_CONVERSION]: envBoolean(
    'REACT_APP_USE_TRANSPOSE_MODE_CONVERSION',
    false,
  ),
  [FeatureFlag.USE_HISTORY_OF_PIANO_MUSIC]: envBoolean(
    'REACT_APP_USE_HISTORY_OF_PIANO_MUSIC',
    false,
  ),
};

const PRODUCTION_FORCED_OFF_FLAGS = new Set<FeatureFlag>([
  FeatureFlag.USE_AUDIO_RECOGNITION,
  FeatureFlag.USE_REPERTOIRE_CREATE,
  FeatureFlag.USE_REPERTOIRE_LIBRARY,
  FeatureFlag.USE_TRANSPOSE_MODE_CONVERSION,
]);

function isProductionBuild(): boolean {
  return process.env.REACT_APP_NODE_ENV === 'prd';
}

function isForcedOffInProduction(flag: FeatureFlag): boolean {
  return isProductionBuild() && PRODUCTION_FORCED_OFF_FLAGS.has(flag);
}

/**
 * Local storage key for feature flags
 */
const STORAGE_KEY = 'museflow_feature_flags';

/**
 * Get all feature flags from local storage
 */
function getStoredFlags(): Partial<Record<FeatureFlag, boolean>> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Failed to read feature flags from local storage:', error);
  }
  return {};
}

/**
 * Save feature flags to local storage
 */
function saveFlags(flags: Partial<Record<FeatureFlag, boolean>>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(flags));
  } catch (error) {
    console.warn('Failed to save feature flags to local storage:', error);
  }
}

function notifyFeatureFlagsChanged(): void {
  window.dispatchEvent(new Event(FEATURE_FLAGS_CHANGED_EVENT));
}

/**
 * Check if a feature flag is enabled
 */
export function isFeatureEnabled(flag: FeatureFlag): boolean {
  if (isForcedOffInProduction(flag)) {
    return false;
  }

  // Check URL parameter first (for testing)
  const urlParams = new URLSearchParams(window.location.search);
  const urlValue = urlParams.get(flag);
  if (urlValue !== null) {
    return urlValue === 'true' || urlValue === '1';
  }

  // Check local storage
  const storedFlags = getStoredFlags();
  if (flag in storedFlags) {
    return storedFlags[flag]!;
  }

  // Return default value
  return DEFAULT_FLAGS[flag];
}

/**
 * Set a feature flag value
 */
export function setFeatureFlag(flag: FeatureFlag, enabled: boolean): void {
  if (isForcedOffInProduction(flag)) {
    return;
  }

  const storedFlags = getStoredFlags();
  storedFlags[flag] = enabled;
  saveFlags(storedFlags);
  notifyFeatureFlagsChanged();
}

/**
 * Reset a feature flag to its default value
 */
export function resetFeatureFlag(flag: FeatureFlag): void {
  const storedFlags = getStoredFlags();
  delete storedFlags[flag];
  saveFlags(storedFlags);
  notifyFeatureFlagsChanged();
}

/**
 * Reset all feature flags to their default values
 */
export function resetAllFeatureFlags(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    notifyFeatureFlagsChanged();
  } catch (error) {
    console.warn('Failed to reset feature flags:', error);
  }
}

/**
 * Get all current feature flag values
 */
export function getAllFeatureFlags(): Record<FeatureFlag, boolean> {
  const result: Record<FeatureFlag, boolean> = { ...DEFAULT_FLAGS };
  const storedFlags = getStoredFlags();

  for (const flag of Object.values(FeatureFlag)) {
    if (isForcedOffInProduction(flag)) {
      result[flag] = false;
    } else if (flag in storedFlags) {
      result[flag] = storedFlags[flag]!;
    }
  }

  return result;
}

/**
 * Hook for using feature flags in React components
 * Note: This is a simple implementation. For real-time updates,
 * consider using a context provider or state management solution.
 */
export function useFeatureFlag(flag: FeatureFlag): boolean {
  const [enabled, setEnabled] = useState(() => isFeatureEnabled(flag));

  useEffect(() => {
    const updateEnabled = () => setEnabled(isFeatureEnabled(flag));

    window.addEventListener(FEATURE_FLAGS_CHANGED_EVENT, updateEnabled);
    window.addEventListener('storage', updateEnabled);

    return () => {
      window.removeEventListener(FEATURE_FLAGS_CHANGED_EVENT, updateEnabled);
      window.removeEventListener('storage', updateEnabled);
    };
  }, [flag]);

  return enabled;
}

/**
 * Convenience function to check if OSMD Phaser backend should be used
 */
export function useOSMDPhaserBackend(): boolean {
  return isFeatureEnabled(FeatureFlag.USE_OSMD_PHASER_BACKEND);
}
